/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LmsShell, studentApi, setStudentSession } from "./portal/shell";

/* --------------------------------------------------------------------
   Password strength scoring — lightweight entropy heuristic. Not NIST,
   but gets the common failure modes right: too short, all-letters, all-
   lowercase, "password123" patterns. Returns 0..4 with a label + hint.
   -------------------------------------------------------------------- */
function scorePassword(pw) {
  if (!pw) return { score: 0, label: "", color: "#1c2a27", hint: "" };
  let score = 0;
  const length = pw.length;

  // Length tiers.
  if (length >= 8) score += 1;
  if (length >= 12) score += 1;
  if (length >= 16) score += 1;

  // Character classes.
  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSymbol = /[^A-Za-z0-9]/.test(pw);
  const classes = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;
  if (classes >= 3) score += 1;

  // Common weak patterns — penalize.
  const weak = /^(password|qwerty|letmein|welcome|admin|12345|abcdef)/i;
  if (weak.test(pw)) score = Math.min(score, 1);
  // All-same-char or sequential (aaaa, 1234, abcd).
  if (/^(.)\1+$/.test(pw) || /^(?:0?1?2?3?4?5?6?7?8?9?)+$/.test(pw)) score = 1;

  score = Math.max(0, Math.min(4, score));

  const presets = [
    { label: "Very weak", color: "#f47171", hint: "Too short or too common." },
    { label: "Weak",      color: "#f0892c", hint: "Add length or mix in digits and symbols." },
    { label: "Okay",      color: "#f0be3c", hint: "Decent — try 12+ characters with a symbol." },
    { label: "Strong",    color: "#9ac84a", hint: "Good. One more class or longer and it's great." },
    { label: "Very strong", color: "#3dc0af", hint: "Excellent." },
  ];
  return { score, ...presets[score] };
}

function StrengthMeter({ pw }) {
  const s = useMemo(() => scorePassword(pw), [pw]);
  const segs = [0, 1, 2, 3];
  // Reactive narrow-screen flag — follows orientation changes + browser
  // resize so the hint always sits where it reads best.
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 480
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setIsNarrow(window.innerWidth < 480);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!pw) {
    return (
      <div data-testid="set-password-strength-empty"
        style={{ font: "500 11px 'JetBrains Mono', monospace",
          color: "#7b8b87", marginTop: 6, marginBottom: 10,
          letterSpacing: "0.03em" }}>
        Password strength: enter a password to score it.
      </div>
    );
  }
  return (
    <div data-testid="set-password-strength"
      style={{ marginTop: 6, marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
        {segs.map((i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i < s.score ? s.color : "#1c2a27",
            transition: "background 160ms",
          }} />
        ))}
      </div>
      <div style={{
        display: "flex",
        flexDirection: isNarrow ? "column" : "row",
        justifyContent: isNarrow ? "flex-start" : "space-between",
        alignItems: isNarrow ? "flex-start" : "baseline",
        gap: isNarrow ? 2 : 10,
        font: "500 11px 'JetBrains Mono', monospace",
        letterSpacing: "0.03em", color: "#7b8b87", lineHeight: 1.45,
      }}>
        <span data-testid="set-password-strength-label" style={{ color: s.color, fontWeight: 700 }}>
          {s.label}
        </span>
        <span style={{ opacity: 0.8 }}>{s.hint}</span>
      </div>
    </div>
  );
}

export default function SetPassword() {
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (!token) setErr("Missing magic link token."); }, [token]);

  const strength = scorePassword(pw);
  // Enforce "Okay" or better on submit — matches the backend's min-length
  // guarantee while nudging the student toward something they won't lose
  // to a drive-by brute force.
  const tooWeak = pw.length > 0 && strength.score < 2;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (tooWeak) return setErr("Please choose a stronger password — add length, digits, or a symbol.");
    if (pw !== pw2) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      const data = await studentApi.setPassword(token, pw);
      setStudentSession(data);
      nav("/dashboard", { replace: true });
    } catch (ex) {
      setErr(ex.message || "Failed to set password. Your link may have expired.");
    } finally { setBusy(false); }
  };

  return (
    <LmsShell subtitle="Set your password" data-testid="set-password-page">
      <form className="tbg-lms-card" onSubmit={submit} data-testid="set-password-form">
        <div className="h-sub">Advanced RPAS · Enrolment</div>
        <h2>Set your password</h2>
        <p>Almost in. Pick a password to activate your account.</p>

        <label className="tbg-lms-lbl">Password<span className="req">*</span></label>
        <input
          type="password" required minLength={8}
          value={pw} onChange={(e) => setPw(e.target.value)}
          className="tbg-lms-input" placeholder="Minimum 8 characters"
          data-testid="set-password-pw" autoFocus
        />
        <StrengthMeter pw={pw} />
        <label className="tbg-lms-lbl">Confirm password<span className="req">*</span></label>
        <input
          type="password" required minLength={8}
          value={pw2} onChange={(e) => setPw2(e.target.value)}
          className="tbg-lms-input" placeholder="Re-enter password"
          data-testid="set-password-pw2"
        />
        {err && <div className="tbg-lms-err" data-testid="set-password-error">{err}</div>}
        <button
          type="submit" className="tbg-lms-btn"
          disabled={busy || !token || tooWeak} data-testid="set-password-submit"
        >
          {busy ? "Setting password…" : "Set password & enter"}
        </button>
        <div className="tbg-lms-foot">
          <Link to="/login">Already set? Log in</Link>
          <Link to="/#quote">Need help?</Link>
        </div>
      </form>
    </LmsShell>
  );
}
